import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { HandHeart, Search, ChevronDown, Trash2, User, Mail, Calendar, EyeOff, Bell, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-moj p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl gradient-purple flex items-center justify-center">
        <Icon size={22} className="text-moj-gold" />
      </div>
      <div>
        <div className="text-2xl font-bold text-moj-purple-deep">{value}</div>
        <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold">{label}</div>
      </div>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  if (typeof ts !== 'string' || !ts.includes('T')) return ts;
  const [d, t] = ts.split('T');
  return `${d} · ${t.slice(0, 5)}`;
}

function PrayerRow({ prayer, isOpen, onToggle, onDelete, onPing, isPinging }) {
  const displayName = prayer.is_anonymous
    ? 'Anonymous'
    : (prayer.full_name || 'Unnamed');
  const hasName = !prayer.is_anonymous && !!prayer.full_name;
  const pinged = Boolean(prayer.last_pinged_at);

  return (
    <div className="card-moj overflow-hidden" data-testid={`prayer-row-${prayer.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-4 hover:bg-moj-cream-soft transition flex items-center gap-4"
        aria-expanded={isOpen}
      >
        <div className="w-10 h-10 rounded-full gradient-purple flex items-center justify-center flex-shrink-0">
          {prayer.is_anonymous ? (
            <EyeOff size={16} className="text-moj-gold" />
          ) : (
            <span className="font-serif text-moj-gold text-sm">
              {displayName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-moj-purple-deep flex items-center gap-2">
            {displayName}
            {prayer.is_anonymous && (
              <span className="text-[10px] uppercase tracking-wider font-bold bg-moj-purple/10 text-moj-purple-deep px-1.5 py-0.5 rounded">
                Anonymous
              </span>
            )}
          </div>
          <div className="text-xs text-moj-charcoal/60 flex flex-wrap gap-x-3 mt-0.5">
            {prayer.email && !prayer.is_anonymous ? (
              <span className="inline-flex items-center gap-1">
                <Mail size={12} /> {prayer.email}
              </span>
            ) : (
              <span className="text-moj-charcoal/40 italic">No email provided</span>
            )}
            <span className="inline-flex items-center gap-1">
              <Calendar size={12} /> {formatDate(prayer.created_at)}
            </span>
          </div>
        </div>
        <p className="hidden md:block text-xs text-moj-charcoal/60 text-right max-w-[280px] italic truncate">
          &ldquo;{prayer.request}&rdquo;
        </p>
        <ChevronDown size={18} className={`text-moj-gold-deep transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="px-5 pb-5 pt-3 bg-moj-cream-soft border-t border-moj-gold/20 space-y-4 text-sm"
          data-testid={`prayer-details-${prayer.id}`}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1 flex items-center gap-1">
                <User size={12} /> Full Name
              </div>
              <div className="text-moj-purple-deep font-semibold" data-testid={`prayer-name-${prayer.id}`}>
                {hasName ? prayer.full_name : (prayer.is_anonymous ? 'Anonymous' : 'Not provided')}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1 flex items-center gap-1">
                <Mail size={12} /> Email
              </div>
              <div className="text-moj-purple-deep font-semibold break-all" data-testid={`prayer-email-${prayer.id}`}>
                {prayer.email
                  ? (
                    <a href={`mailto:${prayer.email}`} className="hover:underline">
                      {prayer.email}
                    </a>
                  )
                  : <span className="text-moj-charcoal/50 italic font-normal">Not provided</span>}
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1 flex items-center gap-1">
              <HandHeart size={12} /> Prayer Request
            </div>
            <div
              className="mt-1 rounded-lg bg-white border border-moj-gold/30 p-4 text-moj-charcoal whitespace-pre-wrap leading-relaxed"
              data-testid={`prayer-request-${prayer.id}`}
            >
              {prayer.request || <span className="italic text-moj-charcoal/50">(No content)</span>}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-moj-gold/20 text-xs text-moj-charcoal/60 flex-wrap">
            <span className="flex items-center gap-2 flex-wrap">
              Submitted {formatDate(prayer.created_at)}
              {pinged && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 font-semibold uppercase tracking-wide text-[10px]"
                  data-testid={`prayer-pinged-${prayer.id}`}
                  title={`Pinged ${formatDate(prayer.last_pinged_at)}${prayer.last_pinged_by ? ' by ' + prayer.last_pinged_by : ''}`}
                >
                  <CheckCircle2 size={10}/> Pinged · {prayer.last_ping_stats?.sent ?? 0} prayed
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const note = window.prompt(
                    'Optional short note for the prayer ping (leave blank for the default message).\n\nPrayer request text is NEVER sent — only this note.',
                    '',
                  );
                  if (note === null) return; // cancelled
                  onPing(prayer.id, note.trim());
                }}
                disabled={isPinging}
                data-testid={`prayer-ping-btn-${prayer.id}`}
                className="px-3 py-1.5 rounded bg-moj-purple text-white flex items-center gap-1 hover:brightness-110 disabled:opacity-60"
              >
                {isPinging
                  ? <><Loader2 size={12} className="animate-spin"/> Sending…</>
                  : <><Bell size={12}/> {pinged ? 'Ping again' : 'Send prayer ping'}</>
                }
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Delete this prayer request from ${displayName}?`)) onDelete(prayer.id);
                }}
                data-testid={`prayer-delete-btn-${prayer.id}`}
                className="px-3 py-1.5 rounded bg-red-600 text-white flex items-center gap-1 hover:bg-red-700"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PrayersTab() {
  const qc = useQueryClient();
  const { data: prayers = [], isLoading } = useQuery({
    queryKey: ['adm-prayers'],
    queryFn: async () => (await api.get('/admin/prayer-requests')).data,
  });

  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/prayer-requests/${id}`)).data,
    onSuccess: () => {
      toast.success('Prayer request deleted');
      qc.invalidateQueries({ queryKey: ['adm-prayers'] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || 'Could not delete prayer request'),
  });

  // Fire a push ping to subscribers who opted in to prayer notifications.
  // The admin picks the note; the raw prayer text is NEVER sent over push
  // (privacy-first: lock-screen previews can't leak personal detail).
  const ping = useMutation({
    mutationFn: async ({ id, note }) =>
      (await api.post(`/admin/push/prayer-ping/${id}`, { note: note || null })).data,
    onSuccess: (data) => {
      toast.success(
        `Prayer ping sent to ${data.sent} of ${data.attempted} opted-in device${data.attempted === 1 ? '' : 's'}.`,
      );
      qc.invalidateQueries({ queryKey: ['adm-prayers'] });
      qc.invalidateQueries({ queryKey: ['adm-push-history'] });
      qc.invalidateQueries({ queryKey: ['adm-push-stats'] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || 'Could not send prayer ping'),
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return prayers;
    const q = search.toLowerCase();
    return prayers.filter((p) =>
      (p.full_name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.request || '').toLowerCase().includes(q)
    );
  }, [prayers, search]);

  const stats = useMemo(() => {
    const total = prayers.length;
    const anonymous = prayers.filter((p) => p.is_anonymous).length;
    const withContact = prayers.filter((p) => !p.is_anonymous && p.email).length;
    return { total, anonymous, withContact };
  }, [prayers]);

  return (
    <div className="space-y-6">
      <div className="card-moj p-5">
        <div className="flex items-center gap-3 mb-2">
          <HandHeart size={22} className="text-moj-gold-deep" />
          <h2 className="font-serif text-xl text-moj-purple-deep">Prayer Requests</h2>
        </div>
        <p className="text-sm text-moj-charcoal/70">
          Every prayer submission from the church website. Click a row to read the full
          request. Treat all requests as pastorally confidential.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard label="Total received" value={stats.total} icon={HandHeart} />
        <StatCard label="With contact email" value={stats.withContact} icon={Mail} />
        <StatCard label="Anonymous" value={stats.anonymous} icon={EyeOff} />
      </div>

      <div className="card-moj p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-moj-gold-deep" />
          <input
            className="input-moj pl-9"
            placeholder="Search by name, email or request…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="prayer-search"
          />
        </div>
        <div className="text-sm text-moj-charcoal/70">
          {filtered.length} of {prayers.length}{' '}
          {prayers.length === 1 ? 'request' : 'requests'}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-moj-charcoal/60">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-moj p-10 text-center text-moj-charcoal/60">
          {prayers.length === 0
            ? 'No prayer requests received yet.'
            : 'No prayer requests match your search.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <PrayerRow
              key={p.id}
              prayer={p}
              isOpen={openId === p.id}
              onToggle={() => setOpenId((cur) => (cur === p.id ? null : p.id))}
              onDelete={del.mutate}
              onPing={(id, note) => ping.mutate({ id, note })}
              isPinging={ping.isPending && ping.variables?.id === p.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
